import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Search, MapPin, BookOpen, Filter,
  ChevronDown, X, Sparkles, Navigation,
  AlertCircle, GraduationCap
} from 'lucide-react';
import apiClient from '../../services/apiClient';
import { TuitionPostCard } from '../../components/cards/TuitionPostCard';

export const BrowseTuitionPosts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // User location for distance calculation
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationReady, setLocationReady] = useState(false);

  const [filters, setFilters] = useState({
    city: '',
    board: 'all',
    class_mode: 'all',
    subject: '',
    budget_min: '',
    budget_max: '',
    availability: 'all',
    sort_by: 'recent',
    distance: 'all'
  });

  // Get user's location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
          setLocationReady(true);
        },
        (error) => {
          console.log('Location access denied or unavailable');
          setLocationLoading(false);
          setLocationReady(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    } else {
      setLocationLoading(false);
      setLocationReady(true);
    }
  };

  // Fetch posts with useCallback to prevent infinite loops
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.board && filters.board !== 'all') params.append('board', filters.board);
      if (filters.class_mode && filters.class_mode !== 'all') params.append('class_mode', filters.class_mode);
      if (filters.subject) params.append('subject', filters.subject);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);

      // Add user location for distance calculation if available
      if (userLocation && userLocation.lat && userLocation.lng) {
        params.append('user_lat', userLocation.lat);
        params.append('user_lng', userLocation.lng);
      }

      const response = await apiClient.get(`/tuition-posts?${params}`);

      if (response && response.posts) {
        setPosts(response.posts);
        setTotal(response.total || response.posts.length);
      } else {
        setPosts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching tuition posts:', error);
      setPosts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters.city, filters.board, filters.class_mode, filters.subject, filters.sort_by, locationReady]);

  // Fetch posts when dependencies change
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = () => {
    fetchPosts();
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      board: 'all',
      class_mode: 'all',
      subject: '',
      budget_min: '',
      budget_max: '',
      availability: 'all',
      sort_by: 'recent',
      distance: 'all'
    });
  };

  // Client-side filtering for budget range and distance
  const filteredPosts = posts.filter(post => {
    // Distance filter
    if (filters.distance !== 'all' && userLocation) {
      const maxDist = parseInt(filters.distance);
      const postDist = post.distance_km;
      if (postDist === null || postDist === undefined || postDist > maxDist) return false;
    }

    // Budget filter
    if (filters.budget_min && post.budget_amount < parseInt(filters.budget_min)) return false;
    if (filters.budget_max && post.budget_amount > parseInt(filters.budget_max)) return false;

    // Availability filter
    if (filters.availability !== 'all' && post.availability !== filters.availability) return false;

    return true;
  });

  const viewDetails = (postId) => {
    navigate(`/tuition-posts/${postId}`);
  };

  const TuitionCard = ({ post }) => (
    <TuitionPostCard post={post} onViewDetails={viewDetails} />
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-3">
            Browse Tuition Requirements
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find students looking for tutors in your area and subjects. Start teaching today!
          </p>
        </div>

        {/* Search & Filters */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Main Search Row */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search by city..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="pl-12 h-12 text-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div className="relative flex-1">
                <GraduationCap className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search by subject..."
                  value={filters.subject}
                  onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
                  className="pl-12 h-12 text-lg border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="h-12 px-6"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                <ChevronDown className={`h - 4 w - 4 ml - 2 transition - transform ${showAdvancedFilters ? 'rotate-180' : ''} `} />
              </Button>

              <Select
                value={filters.sort_by}
                onValueChange={(val) => setFilters({ ...filters, sort_by: val })}
              >
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="budget">Highest Budget</SelectItem>
                  {userLocation && <SelectItem value="distance">Nearest First</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Location Status */}
            {!userLocation && !locationLoading && (
              <div className="flex items-center gap-2 text-sm text-amber-600 mb-4">
                <Navigation className="h-4 w-4" />
                <span>Enable location to see distances and sort by nearest</span>
                <Button variant="link" size="sm" onClick={getUserLocation} className="text-emerald-600 p-0 h-auto">
                  Enable
                </Button>
              </div>
            )}

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="pt-4 border-t border-gray-100 mt-4">
                <div className="grid md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Board</label>
                    <Select
                      value={filters.board}
                      onValueChange={(val) => setFilters({ ...filters, board: val })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="All Boards" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Boards</SelectItem>
                        <SelectItem value="cbse">CBSE</SelectItem>
                        <SelectItem value="icse">ICSE</SelectItem>
                        <SelectItem value="state_board">State Board</SelectItem>
                        <SelectItem value="international_baccalaureate">IB</SelectItem>
                        <SelectItem value="igcse">IGCSE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Class Mode</label>
                    <Select
                      value={filters.class_mode}
                      onValueChange={(val) => setFilters({ ...filters, class_mode: val })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="All Modes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Modes</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="offline_tutor_place">At Tutor's Place</SelectItem>
                        <SelectItem value="offline_student_place">At Student's Place</SelectItem>
                        <SelectItem value="nearby_classes">Nearby Classes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Availability</label>
                    <Select
                      value={filters.availability}
                      onValueChange={(val) => setFilters({ ...filters, availability: val })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Any Time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Time</SelectItem>
                        <SelectItem value="weekdays">Weekdays</SelectItem>
                        <SelectItem value="weekends">Weekends</SelectItem>
                        <SelectItem value="mornings">Mornings</SelectItem>
                        <SelectItem value="evenings">Evenings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Budget Range</label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.budget_min}
                        onChange={(e) => setFilters({ ...filters, budget_min: e.target.value })}
                        className="h-11"
                      />
                      <span className="text-gray-400">-</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.budget_max}
                        onChange={(e) => setFilters({ ...filters, budget_max: e.target.value })}
                        className="h-11"
                      />
                    </div>
                  </div>

                  {userLocation && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Max Distance</label>
                      <Select
                        value={filters.distance}
                        onValueChange={(val) => setFilters({ ...filters, distance: val })}
                      >
                        <SelectTrigger className="h-11" data-testid="distance-filter">
                          <SelectValue placeholder="Any Distance" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Distance</SelectItem>
                          <SelectItem value="5">Within 5 km</SelectItem>
                          <SelectItem value="10">Within 10 km</SelectItem>
                          <SelectItem value="25">Within 25 km</SelectItem>
                          <SelectItem value="50">Within 50 km</SelectItem>
                          <SelectItem value="100">Within 100 km</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={clearFilters} className="text-gray-600">
                    <X className="h-4 w-4 mr-2" />
                    Clear all filters
                  </Button>
                  <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700">
                    <Search className="h-4 w-4 mr-2" />
                    Apply Filters
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            {loading ? 'Loading...' : (
              <>Showing <span className="font-bold text-gray-900">{filteredPosts.length}</span> tuition requirement{filteredPosts.length !== 1 ? 's' : ''}</>
            )}
          </p>
          <Select defaultValue="recent">
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="budget-high">Highest Budget</SelectItem>
              <SelectItem value="budget-low">Lowest Budget</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Finding tuition opportunities...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <Card className="text-center py-16 border-0 shadow-lg">
            <CardContent>
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tuition posts found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters to find more opportunities</p>
              <Button onClick={clearFilters} variant="outline">
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredPosts.map((post) => (
              <TuitionCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
